import { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  ArrowRightOutlined,
  AudioMutedOutlined,
  BookOutlined,
  ContactsOutlined,
  ContainerOutlined,
  CreditCardOutlined,
  DesktopOutlined,
  DollarOutlined,
  FileTextOutlined,
  FormOutlined,
  PartitionOutlined,
  PlusOutlined,
  PlusSquareOutlined,
  SafetyCertificateOutlined,
  StarFilled,
  StarOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Button, MenuProps, theme, Tooltip } from "antd";
import type { ItemType } from "antd/lib/menu/interface";

import {
  client,
  urlRedirectCommissioningPortal,
  urlRedirectPublicCredit,
} from "@settings";

import { useApiAuthCheckpoint } from "@hooks/api/auth";
import { useClientSettings } from "@hooks/useClientSettings";
import { useCorban } from "@hooks/useCorban";
import useFeatureFlags from "@hooks/useFeatureFlags";
import usePermissions from "@hooks/usePermissions";

import { CorbanSelector } from "./CorbanSelector";
import { Container, Content, Footer, ItemLabel, Menu, Text } from "./styles";

export const SidebarMenu = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const BENEFIT_CARD_NAME = useClientSettings().benefitCardName;
  const { routes } = usePermissions();
  const { featureFlags } = useFeatureFlags();
  const {
    corbanFavorites: favorites,
    removeFavorites,
    addFavorites,
  } = useCorban();
  const {
    token: { colorPrimary },
  } = theme.useToken();

  const [fetchCheckpoint] = useApiAuthCheckpoint();

  const currentRoute = pathname.substring(1);
  const routeSplit = currentRoute.split("/");
  const currentSelectedSub1 = routeSplit[1];
  const currentSelectedSub2 = `${routeSplit[0]}/${routeSplit[1]}`;

  const [openKeys, setOpenKeys] = useState<string[]>([
    currentSelectedSub1,
    currentSelectedSub2,
    "menu-favoritos",
  ]);

  const isAllowedItemType = useCallback(
    (itemType: ItemType) =>
      itemType?.key
        ? !!routes[`/${itemType.key}`.replace(/\/$/, "")]?.canPermission
        : true,
    [routes],
  );

  const handleFetchCheckpoint = useCallback(
    async (url: string) => {
      try {
        const { data: fetchCheckpointData } = await fetchCheckpoint();
        const link = `${url}?slug=${fetchCheckpointData?.slug}`;
        window.open(link, "_blank");
      } catch {
        // do nothing
      }
    },
    [fetchCheckpoint],
  );

  const getAllowedItemTypes = useCallback(
    (
      itemTypeList: ((ItemType & { children?: ItemType[] }) | null)[],
    ): ItemType[] => {
      return itemTypeList.reduce((allowedItemTypes: ItemType[], itemType) => {
        if (itemType && isAllowedItemType(itemType)) {
          if (itemType.children) {
            return [
              ...allowedItemTypes,
              {
                ...itemType,
                children: getAllowedItemTypes(itemType.children),
              },
            ];
          }

          return [...allowedItemTypes, itemType];
        }

        return allowedItemTypes;
      }, []);
    },
    [isAllowedItemType],
  );

  const overrideItem = useCallback(
    (menuItem: ItemType): ItemType => {
      if (menuItem?.type === "submenu" && menuItem.children) {
        menuItem.children = menuItem.children.map(overrideItem);
        menuItem.label = (
          <ItemLabel title={menuItem.label as string}>
            {menuItem.label}
          </ItemLabel>
        );
      }

      if (menuItem?.type === "item" && !menuItem.extra) {
        const isFavorited = favorites.some(
          (favorite) =>
            favorite.path.replace(/\/$/, "") ===
            menuItem.key.toString().replace(/\/$/, ""),
        );
        const Component = isFavorited ? StarFilled : StarOutlined;
        const favoriteMenuItem = {
          name: menuItem.label as string,
          path: menuItem.key as string,
        };
        menuItem.label = (
          <ItemLabel title={menuItem.label as string}>
            {menuItem.label}
          </ItemLabel>
        );
        menuItem.extra = (
          <Tooltip
            title={
              isFavorited ? "Remover dos favoritos" : "Adicionar aos favoritos"
            }
          >
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const favoriteMenuItemList = [favoriteMenuItem];
                if (isFavorited) {
                  removeFavorites(favoriteMenuItemList);
                } else {
                  addFavorites(favoriteMenuItemList);
                }
              }}
            >
              <Component style={{ color: colorPrimary }} />
            </Button>
          </Tooltip>
        );
      }

      return menuItem;
    },
    [addFavorites, colorPrimary, favorites, removeFavorites],
  );

  /**
   * Recursively searches for a specific item in a list of items.
   * @param itemKey The key of the item being searched for.
   * @param items The list of items to search in.
   * @returns The item if found, otherwise undefined.
   */
  const findItemRecursive = useCallback(
    (
      itemKey: NonNullable<ItemType>["key"],
      items: ReadonlyArray<ItemType>,
    ): ItemType | undefined => {
      for (const itemType of items) {
        if (itemType?.type === "item" && itemType?.key === itemKey) {
          return itemType;
        }

        if (itemType?.type === "submenu" && itemType?.children) {
          const result = findItemRecursive(itemKey, itemType.children);
          if (result) return result;
        }
      }

      return undefined;
    },
    [],
  );

  const menuItems = useMemo(() => {
    const children: ItemType[] = [
      {
        type: "submenu",
        label: "Cartão",
        key: "cartao",
        icon: <CreditCardOutlined style={{ color: "#00a2e0" }} />,
        children: [
          {
            label: `Cartão ${BENEFIT_CARD_NAME}`,
            key: "cartao/cartao-beneficio",
            icon: <CreditCardOutlined style={{ color: "#00a2e0" }} />,
            disabled: routes?.["/cartao/cartao-beneficio"]?.isBlocked,
            type: "item",
          },
          {
            label: "Agregação de Margem",
            key: "cartao/agregacao-margem",
            icon: <PlusSquareOutlined style={{ color: "#00a2e0" }} />,
            disabled: routes?.["/cartao/agregacao-margem"]?.isBlocked,
            type: "item",
          },
          {
            label: "Compra de Cliente",
            key: "cartao/compra-cliente",
            icon: <PlusSquareOutlined style={{ color: "#00a2e0" }} />,
            disabled: routes?.["/cartao/compra-cliente"]?.isBlocked,
            type: "item",
          },
          {
            label: "Contratos",
            key: "contratos",
            icon: <DesktopOutlined style={{ color: "#00a2e0" }} />,
            disabled: routes?.["/contratos"]?.isBlocked,
            type: "item",
          },
        ],
      },
      {
        type: "submenu",
        label: client.insuranceName,
        key: "seguros",
        icon: <SafetyCertificateOutlined style={{ color: "#00a2e0" }} />,
        children: [
          {
            label: client.insuranceName,
            key: "seguros/contratar",
            icon: <SafetyCertificateOutlined style={{ color: "#00a2e0" }} />,
            disabled: routes?.["/seguros/contratar"]?.isBlocked,
            type: "item",
          },
          {
            type: "item",
            label: "Planos",
            key: "seguros/listar",
            icon: <FileTextOutlined style={{ color: "#00a2e0" }} />,
            disabled: routes?.["/seguros/listar"]?.isBlocked,
          },
        ],
      },
      {
        type: "submenu",
        label: "Empréstimo",
        key: "consignado",
        icon: (
          <DollarOutlined
            style={{
              color: featureFlags.enableConsignedAmigozColor
                ? "#005882"
                : "#70242B",
            }}
          />
        ),
        children: [
          {
            type: "item",
            label: "Consignado Privado",
            key: "consignado/consignado-privado",
            icon: (
              <FormOutlined
                style={{
                  color: featureFlags.enableConsignedAmigozColor
                    ? "#005882"
                    : "#70242B",
                }}
              />
            ),
            disabled: routes?.["/consignado/consignado-privado"]?.isBlocked,
          },
          {
            type: "item",
            label: "Empréstimo",
            key: "consignado/consignado-publico",
            icon: (
              <FormOutlined
                style={{
                  color: featureFlags.enableConsignedAmigozColor
                    ? "#005882"
                    : "#70242B",
                }}
              />
            ),
            disabled: routes?.["/consignado/consignado-publico"]?.isBlocked,
          },
          {
            type: "item",
            label: "Acompanhamento de proposta",
            key: "consignado/acompanhamento-proposta",
            icon: (
              <DesktopOutlined
                style={{
                  color: featureFlags.enableConsignedAmigozColor
                    ? "#005882"
                    : "#70242B",
                }}
              />
            ),
            disabled:
              routes?.["/consignado/acompanhamento-proposta"]?.isBlocked,
          },
        ],
      },
      {
        type: "item",
        label: "Comissionamento",
        key: "comissionamento",
        icon: (
          <PartitionOutlined
            style={{
              color: featureFlags.enableConsignedAmigozColor
                ? "#005882"
                : "#70242B",
            }}
          />
        ),
        extra: (
          <ArrowRightOutlined
            style={{
              transform: "rotate(-45deg)",
              color: featureFlags.enableConsignedAmigozColor
                ? "#005882"
                : "#70242B",
            }}
          />
        ),
      },
    ];

    const fullMenu: ItemType[] = [
      { type: "divider" },
      {
        type: "submenu",
        label: "Serviços",
        key: "servicos",
        icon: <ContainerOutlined style={{ color: colorPrimary }} />,
        children,
      },
      {
        type: "submenu",
        label: "Usuários",
        key: "menu-usuarios",
        icon: <TeamOutlined style={{ color: colorPrimary }} />,
        children: [
          {
            type: "item",
            label: "Lista de Usuários",
            key: "usuarios",
            icon: (
              <UnorderedListOutlined
                style={{
                  color: colorPrimary,
                }}
              />
            ),
          },
          {
            type: "item",
            label: "Solicitar Novo Usuário",
            key: "usuarios/solicitacao",
            icon: (
              <PlusOutlined
                style={{
                  color: colorPrimary,
                }}
              />
            ),
          },
          {
            type: "item",
            label: "Gerenciamento de usuários",
            key: "usuarios/gerenciar",
            icon: (
              <ContactsOutlined
                style={{
                  color: colorPrimary,
                }}
              />
            ),
          },
        ],
      },
      {
        type: "item",
        label: "Não me perturbe",
        key: "nao-me-perturbe",
        icon: <AudioMutedOutlined style={{ color: colorPrimary }} />,
      },

      {
        label: "Roteiros operacionais",
        key: "roteiros-operacionais",
        icon: <FileTextOutlined style={{ color: colorPrimary }} />,
        type: "item",
      },
      {
        label: "Cursos obrigatórios",
        key: "cursos/obrigatorios",
        icon: <BookOutlined style={{ color: colorPrimary }} />,
        type: "item",
      },
    ];

    fullMenu.splice(1, 0, {
      label: "Favoritos",
      type: "submenu",
      key: "menu-favoritos",
      icon: <StarOutlined style={{ color: colorPrimary }} />,
      children: favorites
        .map((favCorban) => {
          const menuItem = findItemRecursive(favCorban.path, fullMenu);
          if (!menuItem || menuItem.type !== "item") return null;
          return {
            type: "item",
            label: menuItem.label,
            key: menuItem.key + "/",
            disabled: menuItem.disabled,
          } satisfies ItemType;
        })
        .filter((menuItem) => !!menuItem),
    });

    const fullMenuWithIcon = fullMenu.map(overrideItem);

    return getAllowedItemTypes(fullMenuWithIcon);
  }, [
    routes,
    getAllowedItemTypes,
    overrideItem,
    findItemRecursive,
    BENEFIT_CARD_NAME,
    favorites,
    colorPrimary,
    featureFlags.enableConsignedAmigozColor,
  ]);

  const handleClick: MenuProps["onClick"] = ({ key }) => {
    if (["consignado/consignado-publico", "comissionamento"].includes(key)) {
      const url = key.includes("consignado")
        ? urlRedirectPublicCredit
        : urlRedirectCommissioningPortal!;
      void handleFetchCheckpoint(url);
      return;
    }
    const currentKey = key.replace(/\/$/, "");
    if (
      currentRoute.includes(currentKey) &&
      currentKey.includes("consignado/consignado-privado")
    ) {
      window.location.href = `/${currentKey}`;
    } else {
      navigate(`/${currentKey}`);
    }
  };

  return (
    <Container>
      <Content>
        {/* {!hasLayoutImprovement && (
          <LogoLink to={homeRoute}>
            <Logo icon={screens.xs} />
          </LogoLink>
        )} */}
        <CorbanSelector />
        <Menu
          mode="inline"
          items={menuItems}
          onClick={handleClick}
          selectedKeys={[currentRoute]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
        />
      </Content>

      <Footer>
        <Link to="/termos-de-uso">
          <Text>Termos de uso</Text>
        </Link>
        <Link to="/politica-de-privacidade">
          <Text>Política de privacidade</Text>
        </Link>
      </Footer>
    </Container>
  );
};
